/**
 * Google Drive Service
 *
 * Handles all interactions with Google Drive API including:
 * - Authentication (OAuth 2.0)
 * - File/folder management
 * - JSON data read/write with debouncing
 * - File uploads
 *
 * Auth: Uses localStorage for tokens (persists across tabs/sessions, validated on load)
 */

import type { DriveWriteErrorDetail, User } from '@/types';

declare global {
  interface Window {
    gapi: typeof gapi;
    google: {
      accounts: {
        oauth2: {
          initTokenClient: (config: {
            client_id: string;
            scope: string;
            callback: (response: { access_token?: string; error?: string }) => void;
            error_callback?: (error: { type: string; message?: string }) => void;
          }) => { requestAccessToken: (options?: { prompt?: string }) => void };
          revoke: (token: string, callback: () => void) => void;
        };
      };
    };
  }
}

const SCOPES = [
  'https://www.googleapis.com/auth/drive.file',
  'https://www.googleapis.com/auth/userinfo.profile',
  'https://www.googleapis.com/auth/userinfo.email',
].join(' ');
const FOLDER_NAME = 'PersonalInsights';
const UPLOADS_FOLDER_NAME = 'uploads';
const WIKI_FOLDER_NAME = 'wiki';
const WIKI_PAGES_FOLDER_NAME = 'pages';

interface WriteQueueItem {
  data: unknown;
  timeout: ReturnType<typeof setTimeout>;
}

type GoogleTokenPrompt = '' | 'select_account';

class GoogleDriveService {
  private accessToken: string | null = null;
  private appFolderId: string | null = null;
  private uploadsFolderId: string | null = null;
  private wikiFolderId: string | null = null;
  private wikiPagesFolderId: string | null = null;
  private wikiFolderPromise: Promise<string> | null = null;
  private wikiPagesFolderPromise: Promise<string> | null = null;
  private fileIdCache: Map<string, string> = new Map();
  private writeQueue: Map<string, WriteQueueItem> = new Map();
  private readonly WRITE_DEBOUNCE_MS = 500;
  private isInitialized = false;
  private tokenClient: ReturnType<typeof window.google.accounts.oauth2.initTokenClient> | null =
    null;

  // ============================================
  // INITIALIZATION
  // ============================================

  /**
   * Initialize the Google Drive service
   * Loads required scripts and sets up the API client
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    await this.loadScripts();
    await window.gapi.client.init({
      apiKey: import.meta.env.VITE_GOOGLE_API_KEY,
      discoveryDocs: ['https://www.googleapis.com/discovery/v1/apis/drive/v3/rest'],
    });

    // Check for existing session
    const savedToken = localStorage.getItem('google_access_token');
    if (savedToken) {
      this.accessToken = savedToken;
      window.gapi.client.setToken({ access_token: savedToken });
    }

    this.isInitialized = true;
  }

  // ============================================
  // AUTHENTICATION
  // ============================================

  /**
   * Sign in with Google OAuth 2.0
   * @returns User information
   */
  async signIn(): Promise<User> {
    return this.requestToken('select_account');
  }

  /**
   * Attempt a non-interactive sign-in using the user's existing Google session.
   * Used during bootstrap to restore a session without showing account picker UI.
   */
  async silentSignIn(): Promise<User> {
    return this.requestToken('');
  }

  /**
   * Silently refresh the access token using the cached GIS token client.
   * Requires a prior signIn() so the token client exists.
   * GIS reissues a token without user interaction if Google session is active.
   */
  async refreshToken(): Promise<User> {
    if (!this.tokenClient) {
      throw new Error('No token client — sign in first');
    }

    return this.requestToken('');
  }

  /**
   * Sign out and clear all cached data
   */
  signOut(): void {
    // Don't revoke the OAuth grant — just clear local state.
    // Revoking forces the user to re-consent on next sign-in.
    // Users can revoke via Google Account permissions if needed.

    // Clear all pending writes
    this.writeQueue.forEach(({ timeout }) => clearTimeout(timeout));
    this.writeQueue.clear();

    // Clear state
    this.accessToken = null;
    this.appFolderId = null;
    this.uploadsFolderId = null;
    this.wikiFolderId = null;
    this.wikiPagesFolderId = null;
    this.wikiFolderPromise = null;
    this.wikiPagesFolderPromise = null;
    this.fileIdCache.clear();
    localStorage.removeItem('google_access_token');
    localStorage.removeItem('google_token_expiry');
    localStorage.removeItem('app_folder_id');
    window.gapi.client.setToken(null);
  }

  /**
   * Check if user is signed in
   */
  isSignedIn(): boolean {
    return !!this.accessToken;
  }

  /**
   * Get current access token
   */
  getAccessToken(): string | null {
    return this.accessToken;
  }

  /**
   * Fetch user profile information
   */
  private async fetchUserInfo(): Promise<User> {
    const response = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
      headers: { Authorization: `Bearer ${this.accessToken}` },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch user info');
    }

    const data = (await response.json()) as { email: string; name: string; picture: string };
    return {
      email: data.email,
      name: data.name,
      picture: data.picture,
    };
  }

  private async requestToken(prompt: GoogleTokenPrompt): Promise<User> {
    return new Promise((resolve, reject) => {
      this.tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
        scope: SCOPES,
        callback: (response) => {
          if (response.error) {
            reject(new Error(response.error));
            return;
          }
          if (!response.access_token) {
            reject(new Error('No access token received'));
            return;
          }

          this.accessToken = response.access_token;
          localStorage.setItem('google_access_token', response.access_token);
          localStorage.setItem('google_token_expiry', String(Date.now() + 3600 * 1000));
          window.gapi.client.setToken({ access_token: response.access_token });

          this.fetchUserInfo().then(resolve).catch(reject);
        },
        error_callback: (error) => {
          if (error.type === 'popup_failed_to_open' || error.type === 'popup_closed') {
            // Expected during background token refresh — browsers block popups
            // that aren't triggered by direct user interaction
            console.debug('OAuth popup blocked (expected for silent refresh)');
          } else {
            console.warn('OAuth error:', error);
          }
          reject(new Error(error.message ?? `OAuth error: ${error.type}`));
        },
      });
      this.tokenClient.requestAccessToken({ prompt });
    });
  }

  // ============================================
  // FOLDER MANAGEMENT
  // ============================================

  /**
   * Ensure the app folder exists in Drive
   * Uses localStorage cache and race condition protection
   * @returns Folder ID
   */
  async ensureAppFolder(): Promise<string> {
    if (this.appFolderId) return this.appFolderId;

    // Check localStorage first (persists across sessions)
    const cachedId = localStorage.getItem('app_folder_id');
    if (cachedId) {
      this.appFolderId = cachedId;
      return cachedId;
    }

    // Search for existing folder (use oldest one if multiple exist)
    const response = await window.gapi.client.drive.files.list({
      q: `name='${FOLDER_NAME}' and mimeType='application/vnd.google-apps.folder' and trashed=false`,
      fields: 'files(id)',
      orderBy: 'createdTime',
    });

    if (response.result.files?.length) {
      this.appFolderId = response.result.files[0]?.id ?? null;
      if (this.appFolderId) {
        localStorage.setItem('app_folder_id', this.appFolderId);
        return this.appFolderId;
      }
    }

    // Create new folder
    const created = await window.gapi.client.drive.files.create({
      resource: {
        name: FOLDER_NAME,
        mimeType: 'application/vnd.google-apps.folder',
      },
      fields: 'id',
    });

    // Re-check for race condition (another tab might have created folder)
    const recheck = await window.gapi.client.drive.files.list({
      q: `name='${FOLDER_NAME}' and mimeType='application/vnd.google-apps.folder' and trashed=false`,
      fields: 'files(id)',
      orderBy: 'createdTime',
    });

    // Use the oldest folder (first created wins)
    this.appFolderId = recheck.result.files?.[0]?.id ?? created.result.id ?? null;

    if (this.appFolderId) {
      localStorage.setItem('app_folder_id', this.appFolderId);
    }

    if (!this.appFolderId) {
      throw new Error('Failed to create app folder');
    }
    return this.appFolderId;
  }

  /**
   * Ensure the uploads folder exists
   * @returns Folder ID
   */
  async ensureUploadsFolder(): Promise<string> {
    if (this.uploadsFolderId) return this.uploadsFolderId;

    const appFolderId = await this.ensureAppFolder();

    // Search for existing folder
    const response = await window.gapi.client.drive.files.list({
      q: `name='${UPLOADS_FOLDER_NAME}' and mimeType='application/vnd.google-apps.folder' and '${appFolderId}' in parents and trashed=false`,
      fields: 'files(id)',
      orderBy: 'createdTime',
    });

    if (response.result.files?.length) {
      this.uploadsFolderId = response.result.files[0]?.id ?? null;
      if (this.uploadsFolderId) return this.uploadsFolderId;
    }

    // Create new folder
    const created = await window.gapi.client.drive.files.create({
      resource: {
        name: UPLOADS_FOLDER_NAME,
        mimeType: 'application/vnd.google-apps.folder',
        parents: [appFolderId],
      },
      fields: 'id',
    });

    // Re-check for race condition (another tab might have created the folder first)
    const recheck = await window.gapi.client.drive.files.list({
      q: `name='${UPLOADS_FOLDER_NAME}' and mimeType='application/vnd.google-apps.folder' and '${appFolderId}' in parents and trashed=false`,
      fields: 'files(id)',
      orderBy: 'createdTime',
    });

    this.uploadsFolderId = recheck.result.files?.[0]?.id ?? created.result.id ?? null;
    if (!this.uploadsFolderId) {
      throw new Error('Failed to create uploads folder');
    }
    return this.uploadsFolderId;
  }

  /**
   * Get or create a subfolder within a parent folder.
   * Handles race conditions (another tab creating same folder).
   */
  private async getOrCreateSubfolder(parentId: string, name: string): Promise<string> {
    const response = await window.gapi.client.drive.files.list({
      q: `name='${name}' and mimeType='application/vnd.google-apps.folder' and '${parentId}' in parents and trashed=false`,
      fields: 'files(id)',
      orderBy: 'createdTime',
    });

    if (response.result.files?.length) {
      const id = response.result.files[0]?.id;
      if (id) return id;
    }

    const created = await window.gapi.client.drive.files.create({
      resource: {
        name,
        mimeType: 'application/vnd.google-apps.folder',
        parents: [parentId],
      },
      fields: 'id',
    });

    // Re-check for race condition
    const recheck = await window.gapi.client.drive.files.list({
      q: `name='${name}' and mimeType='application/vnd.google-apps.folder' and '${parentId}' in parents and trashed=false`,
      fields: 'files(id)',
      orderBy: 'createdTime',
    });

    const folderId = recheck.result.files?.[0]?.id ?? created.result.id ?? null;
    if (!folderId) {
      throw new Error(`Failed to create subfolder: ${name}`);
    }
    return folderId;
  }

  /**
   * Ensure the wiki folder exists (PersonalInsights/wiki/).
   * Uses promise memoization to prevent duplicate folder creation
   * when multiple callers race (e.g. two components mounting).
   */
  async ensureWikiFolder(): Promise<string> {
    if (this.wikiFolderId) return this.wikiFolderId;
    if (this.wikiFolderPromise) return this.wikiFolderPromise;
    this.wikiFolderPromise = (async () => {
      try {
        const appFolderId = await this.ensureAppFolder();
        this.wikiFolderId = await this.getOrCreateSubfolder(appFolderId, WIKI_FOLDER_NAME);
        return this.wikiFolderId;
      } finally {
        this.wikiFolderPromise = null;
      }
    })();
    return this.wikiFolderPromise;
  }

  /**
   * Ensure the wiki pages folder exists (PersonalInsights/wiki/pages/).
   * Uses promise memoization like ensureWikiFolder.
   */
  async ensureWikiPagesFolder(): Promise<string> {
    if (this.wikiPagesFolderId) return this.wikiPagesFolderId;
    if (this.wikiPagesFolderPromise) return this.wikiPagesFolderPromise;
    this.wikiPagesFolderPromise = (async () => {
      try {
        const wikiFolderId = await this.ensureWikiFolder();
        this.wikiPagesFolderId = await this.getOrCreateSubfolder(
          wikiFolderId,
          WIKI_PAGES_FOLDER_NAME
        );
        return this.wikiPagesFolderId;
      } finally {
        this.wikiPagesFolderPromise = null;
      }
    })();
    return this.wikiPagesFolderPromise;
  }

  /**
   * List all JSON files in a folder
   */
  async listFilesInFolder(folderId: string): Promise<Array<{ id: string; name: string }>> {
    const response = await window.gapi.client.drive.files.list({
      q: `'${folderId}' in parents and trashed=false and mimeType='application/json'`,
      fields: 'files(id, name)',
      orderBy: 'name',
    });
    return (response.result.files ?? []).map((f) => ({
      id: f.id ?? '',
      name: f.name ?? '',
    }));
  }

  // ============================================
  // JSON READ/WRITE
  // ============================================

  /**
   * Read a JSON file from Drive
   * @param filename - Name of the JSON file
   * @param defaultValue - Default value if file doesn't exist
   * @returns Parsed JSON data
   */
  async readJson<T>(filename: string, defaultValue: T, folderId?: string): Promise<T> {
    const parentId = folderId ?? (await this.ensureAppFolder());

    // Cache key always uses resolved parentId to avoid collision between
    // implicit app-root and explicit folderId referencing the same file
    const cacheKey = `${parentId}/${filename}`;
    let fileId = this.fileIdCache.get(cacheKey);

    if (!fileId) {
      const response = await window.gapi.client.drive.files.list({
        q: `name='${filename}' and '${parentId}' in parents and trashed=false`,
        fields: 'files(id)',
      });

      if (!response.result.files?.length) {
        return defaultValue;
      }

      fileId = response.result.files[0]?.id;
      if (!fileId) {
        return defaultValue;
      }
      this.fileIdCache.set(cacheKey, fileId);
    }

    const content = await window.gapi.client.drive.files.get({
      fileId,
      alt: 'media',
    });

    return JSON.parse(content.body) as T;
  }

  /**
   * Read a JSON file from Drive with cache-busting
   * Uses direct fetch with no-store cache to ensure fresh data
   * Used for background sync to get latest changes from other devices
   * @param filename - Name of the JSON file
   * @param defaultValue - Default value if file doesn't exist
   * @returns Parsed JSON data
   */
  async readJsonFresh<T>(filename: string, defaultValue: T, folderId?: string): Promise<T> {
    const parentId = folderId ?? (await this.ensureAppFolder());

    const cacheKey = `${parentId}/${filename}`;
    let fileId = this.fileIdCache.get(cacheKey);

    if (!fileId) {
      // Use gapi for metadata lookups (file ID doesn't change, caching OK here)
      const response = await window.gapi.client.drive.files.list({
        q: `name='${filename}' and '${parentId}' in parents and trashed=false`,
        fields: 'files(id)',
      });

      if (!response.result.files?.length) {
        return defaultValue;
      }

      fileId = response.result.files[0]?.id;
      if (!fileId) {
        return defaultValue;
      }
      this.fileIdCache.set(cacheKey, fileId);
    }

    // Use direct fetch with cache-busting for file content
    const response = await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${this.accessToken}`,
      },
      cache: 'no-store', // Critical: bypass browser cache entirely
    });

    if (!response.ok) {
      if (response.status === 404) {
        // File was deleted externally, clear from cache
        this.fileIdCache.delete(cacheKey);
        return defaultValue;
      }
      throw new Error(`Failed to read file: ${response.status} ${response.statusText}`);
    }

    const content = await response.text();
    return JSON.parse(content) as T;
  }

  /**
   * Write a JSON file to Drive (debounced)
   * Multiple rapid calls for the same file will be coalesced
   * @param filename - Name of the JSON file
   * @param data - Data to write
   */
  writeJson<T>(filename: string, data: T, folderId?: string): void {
    // For debounce queue, use folderId prefix when provided (wiki calls always provide it)
    const cacheKey = folderId ? `${folderId}/${filename}` : `_root/${filename}`;
    // Cancel pending write for this file
    const pending = this.writeQueue.get(cacheKey);
    if (pending) {
      clearTimeout(pending.timeout);
    }

    // Schedule new write
    const timeout = setTimeout(() => {
      void this.executeWrite(filename, data, folderId);
      this.writeQueue.delete(cacheKey);
    }, this.WRITE_DEBOUNCE_MS);

    this.writeQueue.set(cacheKey, { data, timeout });
  }

  /**
   * Write a JSON file immediately (bypasses debounce)
   * Use for critical operations that must complete
   * @param filename - Name of the JSON file
   * @param data - Data to write
   * @param folderId - Optional parent folder (defaults to app root)
   */
  async writeJsonImmediate<T>(filename: string, data: T, folderId?: string): Promise<void> {
    const cacheKey = folderId ? `${folderId}/${filename}` : `_root/${filename}`;
    // Cancel any pending debounced write
    const pending = this.writeQueue.get(cacheKey);
    if (pending) {
      clearTimeout(pending.timeout);
      this.writeQueue.delete(cacheKey);
    }
    await this.executeWrite(filename, data, folderId);
  }

  /**
   * Execute the actual write operation
   */
  private async executeWrite(filename: string, data: unknown, folderId?: string): Promise<void> {
    const parentId = folderId ?? (await this.ensureAppFolder());
    const content = JSON.stringify(data, null, 2);
    const blob = new Blob([content], { type: 'application/json' });

    const cacheKey = `${parentId}/${filename}`;
    const fileId = this.fileIdCache.get(cacheKey);

    try {
      if (fileId) {
        // Update existing file
        const response = await fetch(
          `https://www.googleapis.com/upload/drive/v3/files/${fileId}?uploadType=media`,
          {
            method: 'PATCH',
            headers: {
              Authorization: `Bearer ${this.accessToken}`,
              'Content-Type': 'application/json',
            },
            body: blob,
          }
        );
        if (!response.ok) {
          throw new Error(`Drive write failed: ${response.status} ${response.statusText}`);
        }
      } else {
        // Create new file
        const metadata = {
          name: filename,
          mimeType: 'application/json',
          parents: [parentId],
        };

        const form = new FormData();
        form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
        form.append('file', blob);

        const response = await fetch(
          'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id',
          {
            method: 'POST',
            headers: { Authorization: `Bearer ${this.accessToken}` },
            body: form,
          }
        );

        if (!response.ok) {
          throw new Error(`Drive create failed: ${response.status} ${response.statusText}`);
        }

        const result = (await response.json()) as { id: string };
        this.fileIdCache.set(cacheKey, result.id);
      }
    } catch (error) {
      // Emit error event for UI to show toast
      const detail: DriveWriteErrorDetail = {
        filename,
        error: error instanceof Error ? error : new Error('Unknown error'),
      };
      window.dispatchEvent(new CustomEvent('drive-write-error', { detail }));
      throw error;
    }
  }

  // ============================================
  // CONFLICT DETECTION
  // ============================================

  /**
   * Check if the Drive folder was modified after last sync
   * @param lastSyncedAt - ISO date string of last sync
   * @returns True if external changes detected
   */
  async checkForExternalChanges(lastSyncedAt: string): Promise<boolean> {
    const appFolderId = await this.ensureAppFolder();

    const response = await window.gapi.client.drive.files.get({
      fileId: appFolderId,
      fields: 'modifiedTime',
    });

    const driveModified = new Date(response.result.modifiedTime ?? 0).getTime();
    const lastSynced = new Date(lastSyncedAt).getTime();

    return driveModified > lastSynced;
  }

  // ============================================
  // FILE UPLOADS
  // ============================================

  /**
   * Upload a file to Drive
   * @param file - File to upload
   * @returns File ID and web view link
   */
  async uploadFile(file: File): Promise<{ fileId: string; webViewLink: string }> {
    const uploadsFolderId = await this.ensureUploadsFolder();

    // Sanitize filename
    const sanitizedName = `file_${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;

    const metadata = {
      name: sanitizedName,
      parents: [uploadsFolderId],
    };

    const form = new FormData();
    form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
    form.append('file', file);

    const response = await fetch(
      'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,webViewLink',
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${this.accessToken}` },
        body: form,
      }
    );

    if (!response.ok) {
      throw new Error('Failed to upload file');
    }

    const result = (await response.json()) as { id: string; webViewLink: string };
    return { fileId: result.id, webViewLink: result.webViewLink };
  }

  /**
   * Delete a file from Drive
   * @param fileId - ID of file to delete
   */
  async deleteFile(fileId: string): Promise<void> {
    await window.gapi.client.drive.files.delete({ fileId });
  }

  // ============================================
  // SCRIPT LOADING
  // ============================================

  /**
   * Load required Google API scripts
   */
  private async loadScripts(): Promise<void> {
    await Promise.all([
      this.loadScript('https://accounts.google.com/gsi/client'),
      this.loadScript('https://apis.google.com/js/api.js'),
    ]);
    await new Promise<void>((resolve) => window.gapi.load('client', resolve));
  }

  /**
   * Load a script dynamically
   */
  private loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if already loaded
      if (document.querySelector(`script[src="${src}"]`)) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
      document.head.appendChild(script);
    });
  }
}

// Export singleton instance
export const googleDrive = new GoogleDriveService();
